import { useCallback, useEffect, useRef, useState, type TouchEvent } from 'react'
import { useQueries } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { listBoards } from '../chatv1/transport'
import type { ProjectRow } from '../transport/api'
import { BoardPane, phoneScreen, usePhone } from './BoardScreen'
import { agentBoard } from './agentBoards'
import { lastBoard, openBoardOn, rememberBoard } from './boardNav'
import './board.css'

const covering = () => window.innerWidth < 900 || phoneScreen()

/**
 * The drawer covers the whole screen, the rail too, rather than the session beside the rail: under 900px (where the rail
 * itself floats over the panes), and on any phone, held either way (owner, 2026-11-02: 手機橫放的時候, 這個白板會切成一半
 * … 直接讓白板佔滿全部的螢幕).
 */
export function useDrawerCovers(): boolean {
  const [covers, setCovers] = useState(covering)
  useEffect(() => {
    const on = () => setCovers(covering())
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return covers
}

/**
 * THE AGENT'S WHITEBOARD, IN ITS SESSION — owner, 2026-11-01: 在一個 Session 裡面新增一個抽屜, 使用者拉出來的時候可以看到
 * 這個 Agent 他所在的白板.
 *
 * The board of a room the agent is in, live, over its conversation: the whole session on a wide screen, the rail left
 * beside it (owner, 2026-11-02: 桌面視窗的 white board 開啟應該也要占滿整個 session 視窗, 不需要覆蓋到左邊的 Side Panel),
 * and the whole screen on a phone held either way (useDrawerCovers). It opens on the board the agent wrote to last
 * (board/agentBoards.ts); the board's name picks another of the room's boards, and the room's name another room when
 * the agent is in several. On a wide screen it opens full screen too — the room's own whiteboard, over the rail as well,
 * where Back comes back to the session.
 */
/**
 * Closed, a drawer gives the keys back where they were before it opened — the composer, as a rule — and not to the page:
 * while it is out they are its own (BoardPane takes them).
 */
export function useFocusBack(): void {
  const before = useRef(document.activeElement)
  useEffect(
    () => () => {
      const was = before.current
      if (was instanceof HTMLElement && was.isConnected && (!document.activeElement || document.activeElement === document.body)) {
        was.focus({ preventScroll: true })
      }
    },
    [],
  )
}

/** A drawer's header, pulled back to the right as its handle was pulled out, closes it. */
export function useDrawerSwipe(onClose: () => void): { onTouchStart: (e: TouchEvent) => void; onTouchMove: (e: TouchEvent) => void } {
  const x = useRef<number | null>(null)
  return {
    onTouchStart: (e: TouchEvent) => {
      x.current = e.touches[0]?.clientX ?? null
    },
    onTouchMove: (e: TouchEvent) => {
      const now = e.touches[0]?.clientX ?? 0
      if (x.current !== null && now - x.current > 64) {
        x.current = null
        onClose()
      }
    },
  }
}

export function BoardDrawer({ hash, rooms, onClose }: { hash: string; rooms: ProjectRow[]; onClose: () => void }) {
  const covers = useDrawerCovers()
  const phone = usePhone()
  useFocusBack()
  const qs = useQueries({
    queries: rooms.map((r) => ({
      queryKey: ['boards', r.project_id],
      queryFn: async () => (await listBoards(r.project_id)).boards,
      staleTime: 15_000,
    })),
  })
  const settled = qs.every((q) => !q.isLoading)
  // Chosen once, when the rooms' boards are known — never moved under the person by the agent's next write.
  const [at, setAt] = useState<{ conv: string; board: string } | null>(null)
  const here = at && rooms.some((r) => r.project_id === at.conv) ? at : null
  const ids = rooms.map((r) => r.project_id)
  const choice = settled && !here ? agentBoard(ids, qs.map((q) => q.data), hash, lastBoard) : null
  const choiceConv = choice?.conv
  const choiceBoard = choice?.board
  useEffect(() => {
    if (choiceConv && choiceBoard) setAt({ conv: choiceConv, board: choiceBoard })
  }, [choiceConv, choiceBoard])
  const shown = here ?? choice
  const pick = useCallback(
    (board: string) =>
      setAt((cur) => {
        if (!cur) return cur
        rememberBoard(cur.conv, board)
        return { conv: cur.conv, board }
      }),
    [],
  )
  const room = rooms.find((r) => r.project_id === shown?.conv)
  const name = (r: ProjectRow) => r.name || 'Project'

  const swipe = useDrawerSwipe(onClose)

  const close = (
    <button type="button" className="bd-back bd-drawer-close" onClick={onClose} aria-label="Close the whiteboard" title="Close">
      <Icon name="chevron_right" size={22} />
    </button>
  )
  if (!shown || !room) {
    return (
      <div className={`bd-screen bd-drawer${covers ? ' bd-cover' : ''}${phone ? ' bd-phone' : ''}`} role="complementary" aria-label="Whiteboard">
        <div className="bd-top" {...swipe}>
          {close}
          <div className="bd-title">Whiteboard</div>
        </div>
        <p className="bd-drawer-note">{settled ? 'This agent is not in a project.' : 'Opening the whiteboard…'}</p>
      </div>
    )
  }
  return (
    <BoardPane
      key={shown.conv}
      convId={shown.conv}
      title={name(room)}
      which={shown.board}
      onPick={pick}
      phone={phone}
      docked
      covers={covers}
      lead={close}
      topSwipe={swipe}
      room={
        rooms.length > 1 ? (
          <select
            className="bd-room-pick"
            aria-label="Project"
            value={shown.conv}
            onChange={(e) => {
              // The agent's board in that room, as on opening.
              const i = ids.indexOf(e.target.value)
              setAt(agentBoard([e.target.value], [qs[i]?.data], hash, lastBoard))
            }}
          >
            {rooms.map((r) => (
              <option key={r.project_id} value={r.project_id}>
                {name(r)}
              </option>
            ))}
          </select>
        ) : undefined
      }
      tail={
        !covers && (
          <button
            type="button"
            className="bd-drawer-full"
            aria-label="Open the whiteboard full screen"
            title="Full screen"
            onClick={() => {
              onClose()
              openBoardOn(shown.conv, shown.board)
            }}
          >
            <Icon name="open_in_new" size={17} />
          </button>
        )
      }
    />
  )
}

/**
 * THE ROOM'S WHITEBOARD, AS A DRAWER — owner, 2026-11-02: 幫我將 白板, Pages (筆記本). 都放在 Bookmark 的上方 … (比放在
 * header 更好). 然後手機窄版頁面也用抽屜打開的方式.
 *
 * Opened from the room's column of corner buttons (or its handle on a phone), the board comes in from the right edge
 * over the room — the rail beside it on a wide screen, the whole screen on a phone — and goes back the same way. It
 * opens on `board` (the one a dot is about, else the one this device had open last); the board's name picks another.
 */
export function RoomBoardDrawer({ convId, title, board, onClose }: { convId: string; title: string; board: string; onClose: () => void }) {
  const covers = useDrawerCovers()
  const phone = usePhone()
  useFocusBack()
  const [which, setWhich] = useState(board)
  const pick = useCallback(
    (b: string) => {
      rememberBoard(convId, b)
      setWhich(b)
    },
    [convId],
  )
  const swipe = useDrawerSwipe(onClose)
  return (
    <BoardPane
      convId={convId}
      title={title}
      which={which}
      onPick={pick}
      phone={phone}
      docked
      covers={covers}
      topSwipe={swipe}
      lead={
        <button type="button" className="bd-back bd-drawer-close" onClick={onClose} aria-label="Close the whiteboard" title="Close">
          <Icon name="chevron_right" size={22} />
        </button>
      }
      tail={
        !covers && (
          <button
            type="button"
            className="bd-drawer-full"
            aria-label="Open the whiteboard full screen"
            title="Full screen"
            onClick={() => {
              onClose()
              openBoardOn(convId, which)
            }}
          >
            <Icon name="open_in_new" size={17} />
          </button>
        )
      }
    />
  )
}
